import React, { useState, useEffect } from 'react';
import './AddExpenses.css';
import { API_URL, getImageUrl } from "./API";

const ExpenseForm = ({ onClose, onSubmit, projectId, employeeId, projectName }) => {
  const [formData, setFormData] = useState({
    name: '', 
    date: '',
    amount: '',
    reason: '',
    photo: null,
    otherDetails: ''
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
  const [currentEmployeeName, setCurrentEmployeeName] = useState('');

  useEffect(() => {
    const loggedInUserId = localStorage.getItem('currentUserId');
    const loggedInUserName = localStorage.getItem('currentUserName');
    
    if (loggedInUserId) {
      const numericEmployeeId = parseInt(loggedInUserId);
      setCurrentEmployeeId(numericEmployeeId);
      setFormData(prev => ({
        ...prev,
        name: loggedInUserName || ''
      }));
      setCurrentEmployeeName(loggedInUserName || '');
    } else if (employeeId) {
      const numericEmployeeId = parseInt(employeeId);
      setCurrentEmployeeId(numericEmployeeId);
    }
  }, [employeeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'application/pdf'];
      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.pdf'];
      
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        alert('Invalid file type. Please upload a PNG, JPG, JPEG, GIF, or PDF file.');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        photo: file
      }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoRemove = () => {
    setFormData(prev => ({
      ...prev,
      photo: null
    }));
    setPhotoPreview(null);
    
    const fileInput = document.getElementById('photo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const submitToBackend = async (expenseData) => {
    const formDataToSend = new FormData();
    
    const numericProjectId = parseInt(projectId);
    const numericEmployeeId = parseInt(currentEmployeeId);
    
    formDataToSend.append('project_id', numericProjectId);
    formDataToSend.append('employee_id', numericEmployeeId);
    formDataToSend.append('expense_reason', expenseData.reason || '');
    formDataToSend.append('date_of_expense', expenseData.date || '');
    formDataToSend.append('amount', parseFloat(expenseData.amount) || 0);
    formDataToSend.append('other_details', expenseData.otherDetails || '');
    
    if (expenseData.photo) {
      const file = expenseData.photo;
      
      
      formDataToSend.append('expense_image', file, file.name);
    }

    try {
      const response = await fetch(`${API_URL}/projects/create_update_project_expense`, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
        } catch {
          errorMessage = errorText || `HTTP error! status: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      
      throw error;
    }
  };

  const sendNotificationToAdmins = async () => {
    const currentUserName = localStorage.getItem('currentUserName');
    const currentUserId = localStorage.getItem('currentUserId');
    
    try {
      const response = await fetch(`${API_URL}/employees/get_employees`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (response.ok) {
        const result = await response.json();
        let employees = [];
        
        if (Array.isArray(result)) {
          employees = result;
        } else if (result.employees && Array.isArray(result.employees)) {
          employees = result.employees;
        }

        const adminUsers = employees.filter(emp => {
          const role = (emp.role || '').toUpperCase();
          const empId = emp.id || emp.employee_id;
          return (role.includes('CEO') || 
                 role.includes('ADMIN') || 
                 role.includes('ASSISTANT ADMIN')) &&
                 empId !== parseInt(currentUserId);
        });

     

        adminUsers.forEach(admin => {
          const adminId = admin.id || admin.employee_id;
          
          const notification = {
            id: `expense_${Date.now()}_${Math.random()}`,
            type: 'expense_request',
            message: `${currentUserName} submitted an expense of ₹${formData.amount} for "${formData.reason}"`,
            details: {
              employee: currentUserName,
              employeeId: currentUserId,
              amount: formData.amount,
              reason: formData.reason,
              project: projectName || 'Unknown Project',
              date: formData.date
            },
            timestamp: new Date().toISOString(),
            read: false
          };

          const existingNotifications = JSON.parse(
            localStorage.getItem(`notifications_${adminId}`) || '[]'
          );
          existingNotifications.unshift(notification);
          
          localStorage.setItem(
            `notifications_${adminId}`,
            JSON.stringify(existingNotifications)
          );

         
        });

        window.dispatchEvent(new Event('notificationUpdate'));
        window.dispatchEvent(new Event('storage'));
        
       
      } else {
      
      }
    } catch (error) {
      
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectId) {
      alert('Project ID is missing. Please ensure you opened this form from a specific project.');
      return;
    }

    if (!currentEmployeeId) {
      alert('Employee ID is missing. Please ensure you are logged in properly.');
      return;
    }

    if (!formData.date) {
      alert('Please select a date for the expense.');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Please enter a valid amount.');
      return;
    }

    if (!formData.reason || formData.reason.trim() === '') {
      alert('Please enter a reason for the expense.');
      return;
    }

    const numericProjectId = parseInt(projectId);
    const numericEmployeeId = parseInt(currentEmployeeId);
    
    if (isNaN(numericProjectId) || isNaN(numericEmployeeId)) {
      alert('Invalid project or employee ID format.');
      return;
    }

    setIsSubmitting(true);

    try {
      const backendResponse = await submitToBackend(formData);
      
      await sendNotificationToAdmins();
      
      const imageUrl = backendResponse.expense_image 
        ? getImageUrl(backendResponse.expense_image)
        : null;
      
    
      
      const expenseForFrontend = {
        id: backendResponse.expense_id || backendResponse.id || Date.now(),
        expense_id: backendResponse.expense_id || backendResponse.id,
        employeeName: formData.name,  
        employee_name: formData.name,
        date: formData.date,
        amount: parseFloat(formData.amount || 0),
        reason: formData.reason,
        description: formData.reason,
        status: 'Pending',
        expense_image: imageUrl,
        expense_image_path: backendResponse.expense_image,
        expenseImage: imageUrl,
        image_url: imageUrl,
        photo: imageUrl,
        createdAt: new Date().toISOString(),
        projectId: numericProjectId,
        project_id: numericProjectId,
        employeeId: numericEmployeeId,
        employee_id: numericEmployeeId,
        other_details: formData.otherDetails,
        ...backendResponse
      };

      if (onSubmit) {
        onSubmit(expenseForFrontend);
      }

      setFormData({
        name: currentEmployeeName,
        date: '',
        amount: '',
        reason: '',
        photo: null,
        otherDetails: ''
      });
      setPhotoPreview(null);
      
      const fileInput = document.getElementById('photo-upload');
      if (fileInput) {
        fileInput.value = '';
      }

      alert(`Expense submitted successfully for ${projectName || 'Project'}!`);
      
      if (onClose) {
        onClose();
      }

    } catch (error) {
     
      alert(`Failed to submit expense: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="AEXP-00">
      <div className="AEXP-01">
        <button className="AEXP-02" onClick={handleClose}>
          ×
        </button>
        <br />
        
        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
          <strong>Project: {projectName || 'Unknown Project'}</strong>
          <br />
          <small>Project ID: {projectId || 'Not Available'}</small>
          <br />
          <small>Employee: {currentEmployeeName || 'Not Available'} (ID: {currentEmployeeId || 'Not Available'})</small>
          {(!projectId || !currentEmployeeId) && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
              ⚠️ Warning: Missing required information. Please ensure you are logged in properly.
            </div>
          )}
        </div>
        
        <br />
        <form className="AEXP-07" onSubmit={handleSubmit}>
          <div className="AEXP-08">
            <div className="AEXP-09">
              <label className="AEXP-10">Employee Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleInputChange}
                className="AEXP-12"
                autoComplete="off"
                disabled={isSubmitting}
                readOnly
                style={{ backgroundColor: '#f9f9f9', cursor: 'not-allowed' }}
              />
            </div>
            <div className="AEXP-09">
              <label className="AEXP-10">Date</label>
              <div className="AEXP-11">
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="AEXP-12"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className="AEXP-08">
            <div className="AEXP-14">
              <label className="AEXP-15">Amount</label>
              <input
                type="number"
                name="amount"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleInputChange}
                className="AEXP-16"
                step="0.01"
                min="0"
                disabled={isSubmitting}
              />
            </div>
            <div className="AEXP-18">
              <label className="AEXP-19">Reason</label>
              <input
                type="text"
                name="reason"
                placeholder="Enter reason for expense"
                value={formData.reason}
                onChange={handleInputChange}
                className="AEXP-20"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="AEXP-08">
            <div className="AEXP-18" style={{width: '100%'}}>
              <label className="AEXP-19">Other Details</label>
              <textarea
                name="otherDetails"
                placeholder="Enter additional details (optional)"
                value={formData.otherDetails}
                onChange={handleInputChange}
                className="AEXP-20"
                rows="3"
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <div className="AEXP-21">
            <label className="AEXP-22">Expense Image</label>
            <div className="AEXP-23">
              {photoPreview ? (
                <div className="AEXP-24">
                  <img src={photoPreview} alt="Preview" className="AEXP-25" />
                  <button 
                    type="button" 
                    className="AEXP-31" 
                    onClick={handlePhotoRemove}
                    title="Remove photo"
                    disabled={isSubmitting}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label htmlFor="photo-upload" className="AEXP-28">
                  <span>+</span>
                  <small>Expense Image</small>
                </label>
              )}
              <input
                type="file"
                id="photo-upload"
                accept=".png,.jpg,.jpeg,.gif,.pdf,image/png,image/jpeg,image/gif,application/pdf"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="AEXP-29"
            disabled={isSubmitting || !projectId || !currentEmployeeId}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Expense'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;